'use client';

import { useLocale } from 'next-intl';
import {
  type ChangeEvent,
  useState,
} from 'react';

import { getCookieCurrency } from '@/features/currency/cookies/cookie-currency';
import {
  DEFAULT_LANGUAGE,
  SUPPORTED_LOCALES,
} from '@/features/language/language-constants';
import { type Language } from '@/features/language/types/language-types';
import { getUserBearerCookie } from '@/features/user/cookies/cookie-user-bearer';
import updateLoggedUserLocalization from '@/features/user/requests/update-logged-user-localization';
import {
  usePathname,
  useRouter,
} from '@/i18n/routing';
import SelectNative from '@/ui/form-elements/select-native/select-native';

interface LanguageSelectProps {
  options: Language[]
}

export default function LanguageSelect(props: LanguageSelectProps) {
  const { options } = props;
  const router = useRouter();
  const pathname = usePathname();
  const locale = useLocale();

  const optionWithCurrentLocale = options.find((option) => option.locale === locale);
  const [selectedLanguage,
    setSelectedLanguage] = useState<Language>(optionWithCurrentLocale ?? DEFAULT_LANGUAGE);

  const setLocalePath = (option: Language) => {
    const currentCurrency = getCookieCurrency();
    const isUserLogged = getUserBearerCookie();

    const switchLanguage = () => {
      setSelectedLanguage(option);
      router.push(pathname, { locale: option.locale });
    };

    if (isUserLogged) {
      updateLoggedUserLocalization(option.locale, currentCurrency)
        .then(() => {
          switchLanguage();
        })
        .catch(() => null);
    } else {
      switchLanguage();
    }
  };

  const onChangeSelect = (event: ChangeEvent<HTMLSelectElement>) => {
    const value = options.find((option) => option.locale === event.target.value);

    if (value && SUPPORTED_LOCALES.includes(value.locale)) {
      setLocalePath(value);
    }
  };

  return (
    <SelectNative
      name="language"
      label="select website language"
      onChange={onChangeSelect}
      size="sm"
      hideBorder
      value={selectedLanguage.locale}
    >
      {options.map((language) => (
        <SelectNative.Option
          key={language.english_name}
          value={language.locale}
          label={language.name}
        >
          {language.name}
        </SelectNative.Option>
      ))}
    </SelectNative>
  );
}
